const STARTUP_RECOVERY_KEY = 'neon-descent:startup-recovery:v1'

export interface RecoveryStorage {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
}

export interface ServiceWorkerRegistrationRecovery {
  unregister(): Promise<boolean>
}

export interface ServiceWorkerRecovery {
  getRegistrations(): Promise<readonly ServiceWorkerRegistrationRecovery[]>
}

export interface CacheRecovery {
  keys(): Promise<readonly string[]>
  delete(key: string): Promise<boolean>
}

export function formatStartupError(error: unknown): string {
  if (error instanceof Error) return error.message
  if (typeof error === 'string') return error
  return String(error)
}

export function shouldAttemptStartupRecovery(storage: RecoveryStorage): boolean {
  if (storage.getItem(STARTUP_RECOVERY_KEY) === '1') return false
  storage.setItem(STARTUP_RECOVERY_KEY, '1')
  return true
}

export function clearStartupRecoveryMarker(storage: Pick<Storage, 'removeItem'>): void {
  storage.removeItem(STARTUP_RECOVERY_KEY)
}

export async function clearOfflineRuntime(
  serviceWorker: ServiceWorkerRecovery | undefined,
  cacheStorage: CacheRecovery | undefined,
): Promise<void> {
  const registrations = await serviceWorker?.getRegistrations() ?? []
  const cacheKeys = await cacheStorage?.keys() ?? []
  await Promise.all([
    ...registrations.map((registration) => registration.unregister()),
    ...cacheKeys.map((key) => cacheStorage?.delete(key)),
  ])
}

export async function requestServiceWorkerRecovery(reload: () => void): Promise<boolean> {
  if (!('serviceWorker' in navigator) || !shouldAttemptStartupRecovery(sessionStorage)) return false
  const registrations = await navigator.serviceWorker.getRegistrations()
  if (registrations.length === 0) return false

  navigator.serviceWorker.addEventListener('controllerchange', reload, { once: true })
  await Promise.all(registrations.map((registration) => registration.update()))
  let updateFound = false
  for (const registration of registrations) {
    const candidate = registration.waiting ?? registration.installing
    if (candidate === null) continue
    updateFound = true
    candidate.postMessage({ type: 'SKIP_WAITING' })
  }
  return updateFound
}
